import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, authedUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChatSocket } from "@/lib/socket";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MessageSquarePlus, Phone, Users, UsersRound } from "lucide-react";
import { toast } from "sonner";
import ChatPane from "@/components/chat/ChatPane";
import { CreateGroupDialog, GroupInfoDialog } from "@/components/chat/GroupDialogs";
import CallOverlay from "@/components/chat/CallOverlay";
import useVoiceCall from "@/hooks/useVoiceCall";

export default function Chat() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [active, setActive] = useState(null); // conversation object
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [empSearch, setEmpSearch] = useState("");
  const [callEvent, setCallEvent] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const isAdmin = user.role === "admin";
  const isGroup = active?.type === "group";

  // This socket carries presence + call signalling; the message thread has its own (ChatPane).
  const { connected, send } = useChatSocket({
    onEvent: (ev) => {
      if (ev.event === "message.new" || ev.event === "conversation.updated") {
        loadConversations().then((list) => {
          // Removed from / left the open group → close it.
          setActive((cur) => (cur && !list.some((c) => c.id === cur.id) ? null : cur));
        });
      } else if (ev.event === "presence") {
        setOnlineUsers((s) => { const n = new Set(s); ev.online ? n.add(ev.user_id) : n.delete(ev.user_id); return n; });
      } else if (ev.event === "presence.snapshot") {
        setOnlineUsers(new Set(ev.online_users));
      } else if (ev.event && ev.event.startsWith("call.")) {
        // Nudge the call hook with a fresh reference each time so its effect fires.
        setCallEvent({ ...ev, __seq: Date.now() + Math.random() });
      }
    },
  });

  const call = useVoiceCall({
    send,
    activeConversation: active,
    currentUserId: user.id,
    incomingEvent: callEvent,
  });

  // Determine which peer to show in the call overlay.
  const callPeer = (() => {
    if (call.phase === "idle" || !call.peerId) return null;
    const inConv = conversations.find((c) => c.other?.id === call.peerId);
    if (inConv?.other) return inConv.other;
    return employees.find((e) => e.id === call.peerId) || { id: call.peerId, full_name: "Contact" };
  })();

  const loadConversations = () => api.get("/chat/conversations").then((r) => { setConversations(r.data); return r.data; });

  useEffect(() => {
    loadConversations();
    api.get("/chat/contacts").then((r) => setEmployees(r.data));
    // eslint-disable-next-line
  }, [user.id]);

  // Deep link from the notification dropdown: /chat?c=<conversation id>
  const deepId = params.get("c");
  useEffect(() => {
    if (!deepId) return;
    (async () => {
      const list = await loadConversations();
      const found = list.find((c) => c.id === deepId);
      if (found) setActive(found);
      setParams({}, { replace: true });
    })();
    // eslint-disable-next-line
  }, [deepId]);

  // Keep the open group's member list fresh (used for names + @mentions).
  const loadGroupMembers = (conv) => {
    if (conv?.type !== "group") return setGroupMembers([]);
    api.get(`/chat/groups/${conv.id}/members`).then((r) => setGroupMembers(r.data)).catch(() => setGroupMembers([]));
  };
  useEffect(() => { loadGroupMembers(active); }, [active?.id, active?.member_count]); // eslint-disable-line

  const startWith = async (emp) => {
    const { data } = await api.post(`/chat/conversations?recipient_id=${emp.id}`);
    setEmpPickerOpen(false); setEmpSearch("");
    await loadConversations();
    setActive(data);
  };

  const filteredEmployees = employees.filter((e) => !empSearch || e.full_name.toLowerCase().includes(empSearch.toLowerCase()));

  return (
    <div className="h-[calc(100vh-140px)] lg:h-[calc(100vh-160px)] flex flex-col rounded-xl hairline overflow-hidden">
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[340px_1fr] min-h-0">
        <aside className="border-r border-border/60 bg-background/60 flex flex-col min-h-0">
          <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between gap-2">
            <div className="font-display font-semibold text-lg">Chats</div>
            <div className="flex items-center gap-1">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${connected ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"}`}>
                {connected ? "online" : "connecting…"}
              </span>
              {isAdmin && (
                <Button data-testid="new-group-btn" onClick={() => setCreateOpen(true)} size="icon" variant="ghost" title="New group"><UsersRound className="w-4 h-4" /></Button>
              )}
              <Button data-testid="new-chat-btn" onClick={() => setEmpPickerOpen((v) => !v)} size="icon" variant="ghost" title="New chat"><MessageSquarePlus className="w-4 h-4" /></Button>
            </div>
          </div>
          {empPickerOpen && (
            <div className="px-3 py-2 border-b border-border/60">
              <Input placeholder="Search…" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} className="mb-2 h-8" />
              <div className="max-h-48 overflow-y-auto scroll-thin space-y-1">
                {filteredEmployees.map((e) => (
                  <button key={e.id} onClick={() => startWith(e)} data-testid={`pick-${e.id}`} className="w-full text-left px-2 py-1 rounded hover:bg-accent flex items-center gap-2 text-sm">
                    <Avatar className="h-6 w-6"><AvatarImage src={authedUrl(e.avatar_url)} /><AvatarFallback className="text-[9px]">{e.full_name.split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                    <span>{e.full_name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto scroll-thin px-2 py-2">
            {conversations.length === 0 && <div className="text-sm text-muted-foreground p-3">No conversations. Start a new chat.</div>}
            {conversations.map((c) => (
              <button key={c.id} onClick={() => setActive(c)} data-testid={`conv-${c.id}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 mb-0.5 transition-colors ${active?.id === c.id ? "bg-primary/10" : "hover:bg-accent"}`}>
                <div className="relative">
                  {c.type === "group" ? (
                    <div className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center"><Users className="w-5 h-5" /></div>
                  ) : (
                    <Avatar className="h-10 w-10"><AvatarImage src={authedUrl(c.other?.avatar_url)} /><AvatarFallback className="text-[10px]">{(c.other?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                  )}
                  {onlineUsers.has(c.other?.id) && <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium truncate">{c.other?.full_name}</div>
                    <div className="text-[10px] text-muted-foreground">{c.last_message_at ? new Date(c.last_message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ""}</div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-muted-foreground truncate">{c.type === "group" && c.last_sender_name && c.last_message ? `${c.last_sender_name}: ${c.last_message}` : (c.last_message || "No messages yet")}</div>
                    {c.unread > 0 && active?.id !== c.id && <Badge className="h-4 min-w-4 px-1 text-[9px]">{c.unread}</Badge>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>

        {!active ? (
          <section className="flex flex-col min-h-0 min-w-0 chat-canvas items-center justify-center text-muted-foreground text-sm gap-2">
            <MessageSquarePlus className="w-10 h-10 opacity-40" />
            <div>Select or start a conversation</div>
          </section>
        ) : (
          <ChatPane
            key={active.id}
            conversation={active}
            user={user}
            contacts={employees}
            members={isGroup ? groupMembers : []}
            isGroup={isGroup}
            onActivity={loadConversations}
            headerLeft={({ typingFrom, typingLabel, onlineUsers: pOnline }) => isGroup ? (
              <>
                <Button variant="ghost" size="icon" onClick={() => setActive(null)} className="lg:hidden"><ArrowLeft className="w-4 h-4" /></Button>
                <button type="button" data-testid="group-header" onClick={() => setInfoOpen(true)} className="flex items-center gap-3 min-w-0 text-left">
                  <div className="h-9 w-9 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center"><Users className="w-4 h-4" /></div>
                  <div className="min-w-0">
                    <div className="font-medium truncate" data-testid="group-header-title">{active.title}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {typingLabel || `${groupMembers.length || active.member_count} members${groupMembers.length ? " · " + groupMembers.map((m) => m.full_name).slice(0, 4).join(", ") + (groupMembers.length > 4 ? "…" : "") : ""}`}
                    </div>
                  </div>
                </button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="icon" onClick={() => setActive(null)} className="lg:hidden"><ArrowLeft className="w-4 h-4" /></Button>
                <Avatar className="h-9 w-9 shrink-0"><AvatarImage src={authedUrl(active.other?.avatar_url)} /><AvatarFallback>{(active.other?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <div className="font-medium truncate">{active.other?.full_name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {typingFrom === active.other?.id ? "typing…" : (onlineUsers.has(active.other?.id) || pOnline.has(active.other?.id)) ? "online" : "offline"}
                  </div>
                </div>
              </>
            )}
            headerExtra={isGroup ? (
              <Button data-testid="group-info-btn" variant="ghost" size="icon" onClick={() => setInfoOpen(true)} title="Group info"><Users className="w-4 h-4" /></Button>
            ) : (
              <Button
                data-testid="chat-voice-call"
                variant="ghost"
                size="icon"
                disabled={!active.other?.id || active.other?.id === "tappy-bot" || call.phase !== "idle"}
                onClick={() => {
                  call.startCall(active, active.other?.id).catch((e) => {
                    toast.error(e?.name === "NotAllowedError" ? "Microphone access denied" : "Could not start call");
                  });
                }}
                title="Voice call"
              >
                <Phone className="w-4 h-4" />
              </Button>
            )}
          />
        )}
      </div>
      <CreateGroupDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        contacts={employees.filter((e) => e.id !== user.id)}
        onCreated={async (g) => { const list = await loadConversations(); setActive(list.find((c) => c.id === g.id) || g); }}
      />
      {isGroup && (
        <GroupInfoDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          conv={active}
          user={user}
          contacts={employees.filter((e) => e.id !== user.id)}
          onChanged={async () => { const list = await loadConversations(); setActive((cur) => list.find((c) => c.id === cur?.id) || cur); loadGroupMembers(active); }}
          onLeft={() => { setActive(null); loadConversations(); }}
        />
      )}
      <CallOverlay
        phase={call.phase}
        peer={callPeer}
        muted={call.muted}
        startedAt={call.startedAt}
        remoteAudioRef={call.remoteAudioRef}
        onAccept={() => call.acceptCall().catch(() => toast.error("Could not accept call"))}
        onDecline={call.declineCall}
        onEndCall={() => call.endCall("completed")}
        onCancelOutgoing={call.cancelOutgoing}
        onToggleMute={call.toggleMute}
      />
    </div>
  );
}
